import { useState } from "react";
import { Task } from "@/types/task-management";
import TaskForm from "./TaskForm";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Plus, Trash2 } from "lucide-react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface TaskDialogProps {
  task?: Task;
  trigger?: React.ReactNode;
  onSubmit: (task: Partial<Task>) => void;
  title?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onDelete?: () => void;
}

export default function TaskDialog({ 
  task, 
  trigger, 
  onSubmit, 
  title = task ? `Editar: ${task.title}` : "Nova Tarefa",
  open: controlledOpen,
  onOpenChange: setControlledOpen,
  onDelete
}: TaskDialogProps) {
  // Estado local se não for controlado externamente
  const [internalOpen, setInternalOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  
  // Determinar se o diálogo é controlado externamente
  const isControlled = controlledOpen !== undefined && setControlledOpen !== undefined;
  
  // Usar estado apropriado
  const open = isControlled ? controlledOpen : internalOpen;
  const setOpen = isControlled ? setControlledOpen : setInternalOpen;
  
  const handleSubmit = (taskData: Partial<Task>) => {
    onSubmit(taskData);
    setOpen(false);
  };
  
  const handleCancel = () => {
    setOpen(false);
  };
  
  const handleDelete = () => {
    if (onDelete) {
      onDelete();
      setOpen(false);
      setConfirmDelete(false);
    }
  };
  
  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent className="sm:max-w-[550px]">
          <DialogHeader>
            <DialogTitle>{title}</DialogTitle>
          </DialogHeader>
          
          <TaskForm 
            task={task} 
            onSubmit={handleSubmit} 
            onCancel={handleCancel}
            extraButtons={
              onDelete && (
                <AlertDialog open={confirmDelete} onOpenChange={setConfirmDelete}>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="destructive" 
                      type="button"
                      className="gap-1"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span>Excluir</span>
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Você tem certeza?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Esta ação não pode ser desfeita. A tarefa será excluída permanentemente.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancelar</AlertDialogCancel>
                      <AlertDialogAction onClick={handleDelete}>
                        Excluir
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )
            }
          />
        </DialogContent>
      </Dialog>
    </>
  );
} 